import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Badge, Box, Button, IconButton, LinearProgress, Popover, Stack, Typography } from '@mui/material';
import { Routes } from 'enums';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import { useUserData } from 'hooks/use-user-data';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import NotificationList from './notification-list';
import { useNotificationActions, useNotificationList, useUnreadCount } from './use-notifications';
import { badgeLabel } from './notification-logic';

/** Presentational bell (testable without DOM). */
export const BellButton: React.FC<{ count: number; onClick?(e: React.MouseEvent<HTMLElement>): void }> = ({ count, onClick }) => {
  const { t } = useTranslation();
  const label = badgeLabel(count);
  return (
    <PaiTooltip title={t('engNotifTitle')}>
      <IconButton size={'small'} onClick={onClick} aria-label={label ? t('engNotifAriaUnread', { count }) : t('engNotifTitle')} data-testid={'notification-bell'}>
        <Badge badgeContent={label} color={'error'} invisible={!label} data-testid={'notification-badge'}>
          <NotificationsNoneIcon sx={{ color: '#555' }} />
        </Badge>
      </IconButton>
    </PaiTooltip>
  );
};

/** Glocke in der Kopfleiste: „Mitteilungen“, never "Inbox" (Posteingang = LinkedIn messages). */
const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useUserData();
  const count = useUnreadCount(token);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const list = useNotificationList(token, 10, !!anchor);
  const { read, readAll, openCta } = useNotificationActions();

  if (!token) return null;

  const items = list.data?.items ?? [];

  return (
    <>
      <BellButton count={count} onClick={(e) => setAnchor(e.currentTarget)} />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 32px)', maxHeight: 520, borderRadius: 2 } } }}
      >
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'greys.lighter' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{t('engNotifTitle')}</Typography>
          <Button size={'small'} sx={{ textTransform: 'none' }} disabled={count === 0 || readAll.isPending} onClick={() => readAll.mutate()}>
            {t('engNotifReadAll')}
          </Button>
        </Stack>
        {list.isLoading ? <LinearProgress /> : null}
        {list.error ? (
          <Typography sx={{ p: 2, fontSize: 13, color: 'error.main' }}>{t('engNotifLoadFailed')}</Typography>
        ) : items.length === 0 && !list.isLoading ? (
          <Typography sx={{ p: 2, fontSize: 13, color: 'text.secondary' }}>{t('engNotifEmpty')}</Typography>
        ) : (
          <NotificationList
            dense
            items={items}
            onMarkRead={(n) => read.mutate(n._id)}
            onOpenCta={(n) => {
              setAnchor(null);
              openCta(n, navigate);
            }}
          />
        )}
        <Box sx={{ borderTop: '1px solid', borderColor: 'greys.lighter', px: 2, py: 1 }}>
          <Button
            size={'small'}
            sx={{ textTransform: 'none' }}
            onClick={() => {
              setAnchor(null);
              navigate(Routes.NOTIFICATIONS);
            }}
          >
            {t('engNotifShowAll')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
